import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import VoiceInput from "./VoiceInput";
import { toast } from "sonner";
import { 
  DollarSign, Send, Zap, Lock, CheckCircle2, Clock, AlertCircle, Loader2, Globe
} from "lucide-react";

export default function BidSubmitCard({ job, user, onBidSubmitted }) {
  const [bidAmount, setBidAmount] = useState(job?.target_price || "");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [bidInfo, setBidInfo] = useState(null);
  const [loadingBidInfo, setLoadingBidInfo] = useState(true);

  useEffect(() => {
    fetchBidInfo();
  }, [job?.id]);

  const fetchBidInfo = async () => {
    if (!job?.id) return;
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/jobs/${job.id}/bids`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBidInfo(response.data);
    } catch (error) {
      console.error("Failed to fetch bid info:", error);
    } finally {
      setLoadingBidInfo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!bidAmount || bidAmount <= 0) {
      toast.error("Please enter a valid bid amount");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/jobs/${job.id}/bids`,
        { bid_amount: parseFloat(bidAmount), message: message || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Bid submitted successfully!");
      if (onBidSubmitted) onBidSubmitted();
      fetchBidInfo(); // Refresh bid info
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit bid");
    } finally {
      setLoading(false);
    }
  };

  if (loadingBidInfo) {
    return (
      <Card className="border-0 shadow-lg">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#1E40AF]" />
        </CardContent>
      </Card>
    );
  }

  const slotsRemaining = (job?.max_bids || 3) - (bidInfo?.job?.bid_count || 0);
  const isBidLocked = bidInfo?.job?.is_bid_locked || job?.is_bid_locked;
  const hasSubmittedBid = bidInfo?.has_submitted_bid;
  const myBid = hasSubmittedBid && bidInfo?.bids?.[0];

  // Show existing bid if already submitted
  if (hasSubmittedBid && myBid) {
    return (
      <Card className={`border-2 ${myBid.status === "accepted" ? "border-green-400 bg-green-50" : myBid.status === "rejected" ? "border-red-300 bg-red-50" : "border-[#1E40AF] bg-[#1E40AF]/5"}`}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            {myBid.status === "accepted" ? (
              <>
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span className="text-green-700">Your Bid Was Accepted!</span>
              </>
            ) : myBid.status === "rejected" ? (
              <>
                <AlertCircle className="w-5 h-5 text-red-500" />
                <span className="text-red-700">Bid Not Selected</span>
              </>
            ) : (
              <>
                <Clock className="w-5 h-5 text-[#1E40AF]" />
                <span className="text-[#1E40AF]">Your Bid is Pending</span>
              </>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-white rounded-lg">
              <span className="text-gray-600">Your Bid Amount</span>
              <span className="text-2xl font-bold text-gray-900">${myBid.bid_amount}</span>
            </div>
            {myBid.message && (
              <div className="p-3 bg-white rounded-lg">
                <span className="text-sm text-gray-500">Your Message:</span>
                <p className="text-gray-700 mt-1">{myBid.message}</p>
              </div>
            )}
            {myBid.status === "accepted" && (
              <div className="p-4 bg-green-100 rounded-lg text-center">
                <p className="text-green-800 font-medium">
                  🎉 Congratulations! The customer selected you. They will contact you soon.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show locked message if bidding is closed
  if (isBidLocked) {
    return (
      <Card className="border-2 border-gray-300 bg-gray-50">
        <CardContent className="p-6 text-center">
          <Lock className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Bidding Closed</h3>
          <p className="text-gray-500">
            This job has reached its maximum number of bids. Better luck on the next one!
          </p>
        </CardContent>
      </Card>
    );
  }

  // Show bid form
  return (
    <Card className="border-2 border-[#1E40AF] shadow-lg">
      <CardHeader className="pb-2 bg-gradient-to-r from-[#1E40AF] to-[#3B82F6] text-white rounded-t-lg">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5" />
            Submit Your Bid
          </div>
          {job?.urgency === "emergency" && (
            <span className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-full text-sm">
              <Zap className="w-4 h-4" />
              {slotsRemaining} of {job?.max_bids || 3} slots left
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Target Price Hint */}
          {job?.pricing_type === "target_price" && job?.target_price && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-700">
                🎯 Customer&apos;s target price: <strong>${job.target_price}</strong>
              </p>
            </div>
          )}

          {/* Bid Amount */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Your Bid Amount</label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="number"
                placeholder="Enter your price"
                value={bidAmount}
                onChange={(e) => setBidAmount(e.target.value)}
                className="h-14 pl-10 text-xl font-semibold"
                min="1"
                step="0.01"
                required
                data-testid="bid-amount-input"
              />
            </div>
          </div>

          {/* Message */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 flex items-center justify-between">
              <span>Message to Customer (Optional)</span>
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <Globe className="w-3 h-3" />
                Auto-translated
              </span>
            </label>
            <div className="relative">
              <Textarea
                placeholder="Speak or type in any language — we'll translate for the customer..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="min-h-[100px] pr-12"
                data-testid="bid-message-input"
              />
              <div className="absolute right-2 bottom-2">
                <VoiceInput 
                  onTranscript={(text) => setMessage(prev => prev ? prev + " " + text : text)}
                  size="sm"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500">
              🌍 Speak in any language — our AI will translate your message for the customer
            </p>
          </div>

          <Button
            type="submit"
            disabled={loading || !bidAmount}
            className="w-full h-12 bg-[#1E40AF] hover:bg-[#1E3A8A] rounded-xl text-base"
            data-testid="submit-bid-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Submit Bid
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
